// Enhanced SAMS Energy Landing Page
// Combines improvements from the 320-line version with restored hero section, form, animations, and accessibility

import React from "react";

const brandColors = {
  green: "#214C28",
  yellow: "#F2A900",
  white: "#FFFFFF",
};

export default function SamsEnergy() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap');
        * { box-sizing: border-box; }
        body { margin: 0; font-family: 'Poppins', sans-serif; background: ${brandColors.white}; color: ${brandColors.green}; }
        a { text-decoration: none; color: inherit; }
        
        header {
          background: ${brandColors.green};
          color: ${brandColors.white};
          text-align: center;
          padding: 1rem 0;
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }
        
        .logo-wrapper {
          background: #fff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 120px;
          height: 120px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        }

        .logo-wrapper img {
          width: 80px;
          height: auto;
        }

        .tagline { font-weight: 600; letter-spacing: 0.05em; }

        .hero {
          background: url('https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=1600&q=80') center/cover no-repeat;
          color: ${brandColors.white};
          text-align: center;
          padding: 6rem 1rem;
          position: relative;
        }

        .hero::after {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(33,76,40,0.75);
        }

        .hero-content { position: relative; z-index: 2; max-width: 800px; margin: auto; }
        .hero h1 { font-size: 2.8rem; margin-bottom: 1rem; }
        .hero h2 { font-size: 1.2rem; font-weight: 400; font-style: italic; margin-bottom: 2rem; }

        .cta-group { display: flex; justify-content: center; gap: 1rem; flex-wrap: wrap; }
        .cta-button {
          background: ${brandColors.yellow};
          border: none;
          color: ${brandColors.green};
          padding: 1rem 2rem;
          border-radius: 50px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.3s ease;
        }
        .cta-button:hover { background: #d18a00; }

        main { max-width: 1200px; margin: 3rem auto; padding: 0 1rem; }
        .section-title { text-align: center; font-size: 2rem; margin-bottom: 1.5rem; }

        .services, .projects { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 2rem; }
        .card {
          background: #f9fdf9;
          border-radius: 12px;
          padding: 1.5rem;
          text-align: center;
          box-shadow: 0 4px 10px rgba(0,0,0,0.1);
          transition: transform 0.3s ease;
        }
        .card:hover { transform: translateY(-5px); }
        .card-icon { font-size: 2.5rem; color: ${brandColors.green}; margin-bottom: 1rem; }

        .testimonials { text-align: center; background: #f5faf5; padding: 3rem 1rem; border-radius: 10px; }

        .contact-section { text-align: center; max-width: 600px; margin: auto; }
        form { display: flex; flex-direction: column; gap: 1rem; margin-top: 1rem; }
        input, textarea { padding: 0.8rem 1rem; border-radius: 8px; border: 1px solid #ccc; font-size: 1rem; }
        button.submit-btn { background: ${brandColors.green}; color: ${brandColors.white}; border: none; padding: 1rem; border-radius: 30px; cursor: pointer; font-weight: 600; }
        button.submit-btn:hover { background: #18381b; }

        footer {
          background: ${brandColors.green};
          color: ${brandColors.white};
          text-align: center;
          padding: 2rem 1rem;
        }
        footer .social a { color: ${brandColors.white}; margin: 0 0.5rem; font-weight: 600; }

        .whatsapp-btn {
          position: fixed;
          bottom: 20px;
          right: 20px;
          background: #25D366;
          border-radius: 50%;
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 10px rgba(0,0,0,0.3);
          z-index: 1000;
        }
        .whatsapp-btn img { width: 32px; height: 32px; }
      `}</style>

      {/* Header */}
      <header>
        <div className="logo-wrapper">
          <img src="/logo.png" alt="SAMS Energy logo" />
        </div>
        <div className="tagline">Powering a Sustainable Future</div>
      </header>

      {/* Hero */}
      <section className="hero">
        <div className="hero-content">
          <h1>Reliable Solar Solutions for Your Home & Business</h1>
          <h2>Sales • Service • Maintenance • Consulting</h2>
          <div className="cta-group">
            <button className="cta-button">Get a Free Quote</button>
            <button className="cta-button">Book a Consultation</button>
          </div>
        </div>
      </section>

      <main>
        {/* Services */}
        <section>
          <h2 className="section-title">Our Services</h2>
          <div className="services">
            <div className="card"><div className="card-icon">☀️</div><h3>Solar System Sales & Design</h3><p>Custom solar power setups tailored to your energy goals and budget.</p></div>
            <div className="card"><div className="card-icon">🔧</div><h3>Installation & Commissioning</h3><p>Expert installation ensuring safety, reliability, and peak efficiency.</p></div>
            <div className="card"><div className="card-icon">🧰</div><h3>Maintenance & Repairs</h3><p>Quick and efficient maintenance to keep your system running smoothly.</p></div>
            <div className="card"><div className="card-icon">📊</div><h3>Consulting & Energy Audits</h3><p>Comprehensive energy analysis to optimize performance and savings.</p></div>
          </div>
        </section>

        {/* Projects */}
        <section>
          <h2 className="section-title">Completed Projects</h2>
          <div className="projects">
            <div className="card"><img src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=600&q=80" alt="Residential Project Dubai" style={{width:"100%", borderRadius:"10px", marginBottom:"1rem"}}/><h3>Residential Installation - Dubai</h3></div>
            <div className="card"><img src="https://images.unsplash.com/photo-1549924231-f129b911e442?auto=format&fit=crop&w=600&q=80" alt="Commercial Project Abu Dhabi" style={{width:"100%", borderRadius:"10px", marginBottom:"1rem"}}/><h3>Commercial Installation - Abu Dhabi</h3></div>
            <div className="card"><img src="https://images.unsplash.com/photo-1515377905703-c4788e51af15?auto=format&fit=crop&w=600&q=80" alt="Maintenance Muscat" style={{width:"100%", borderRadius:"10px", marginBottom:"1rem"}}/><h3>Maintenance Project - Muscat</h3></div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="testimonials">
          <h2 className="section-title">Testimonials</h2>
          <p>Client stories coming soon.</p>
        </section>

        {/* Contact */}
        <section className="contact-section">
          <h2 className="section-title">Contact & Get a Quote</h2>
          <form onSubmit={(e) => { e.preventDefault(); alert('Thank you for contacting SAMS Energy.'); e.target.reset(); }}>
            <input type="text" name="name" placeholder="Your Name" required />
            <input type="email" name="email" placeholder="Email" required />
            <input type="tel" name="phone" placeholder="Phone" required />
            <textarea name="message" placeholder="Your Message" required />
            <button type="submit" className="submit-btn">Request a Quote</button>
          </form>
          <p>Email: <a href="mailto:info@samsenergy.com">info@samsenergy.com</a></p>
          <p>Phone: <a href="tel:+971569798334">+971 569 798 334</a></p>
        </section>
      </main>

      {/* Footer */}
      <footer>
        <div className="logo-wrapper"><img src="/logo.png" alt="SAMS Energy logo" /></div>
        <p>123 Solar Blvd, Dubai, UAE</p>
        <div className="social">
          <a href="https://facebook.com/samsenergy">Facebook</a>|
          <a href="https://instagram.com/samsenergy">Instagram</a>|
          <a href="https://linkedin.com/company/samsenergy">LinkedIn</a>
        </div>
        <p>© 2025 SAMS Energy. All rights reserved.</p>
      </footer>

      {/* WhatsApp */}
      <a href="https://wa.me/971569798334" className="whatsapp-btn" target="_blank" rel="noopener noreferrer">
        <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="WhatsApp" />
      </a>
    </>
  );
}
